/**
 * Typed fetchers for Validation > 2nd line support — the Veeva user manager
 * (`/api/v1/user-admin/*`) and the Administrator approval page
 * (`/api/v1/admin/approvals/veeva/*`). ADR-0038.
 *
 * Types come from `./_generated/schema` (CLAUDE.md §12.8). Regenerate with
 * `pnpm gen:api` after any change to `backend/src/daquma/api/v1/user_admin.py`.
 */

import type { components } from "./_generated/schema";
import { apiFetch } from "./apiFetch";

export type VeevaConnectionOut = components["schemas"]["VeevaConnectionOut"];
export type VeevaUserRowOut = components["schemas"]["VeevaUserRowOut"];
export type VeevaUserListOut = components["schemas"]["VeevaUserListOut"];
export type VeevaUserDetailOut = components["schemas"]["VeevaUserDetailOut"];
export type PicklistsOut = components["schemas"]["PicklistsOut"];
export type PickOut = components["schemas"]["PickOut"];
export type ProposeChangeIn = components["schemas"]["ProposeChangeIn"];
export type ChangeOut = components["schemas"]["ChangeOut"];
export type ChangeStatus = ChangeOut["status"];
export type DecideIn = components["schemas"]["DecideIn"];
export type AgentTurnOut = components["schemas"]["AgentTurnOut"];
export type CreateAgentChatIn = components["schemas"]["CreateAgentChatIn"];

const enc = encodeURIComponent;

export function listVeevaConnections(token: string): Promise<{ items: VeevaConnectionOut[] }> {
  return apiFetch("/api/v1/user-admin/veeva-connections", { token });
}

export function listVeevaUsers(
  token: string,
  connectionId: string,
  search?: string,
): Promise<VeevaUserListOut> {
  const q = search?.trim() ? `?search=${enc(search.trim())}` : "";
  return apiFetch(`/api/v1/user-admin/veeva/${enc(connectionId)}/users${q}`, { token });
}

export function getVeevaUser(
  token: string,
  connectionId: string,
  userId: string,
): Promise<VeevaUserDetailOut> {
  return apiFetch(`/api/v1/user-admin/veeva/${enc(connectionId)}/users/${enc(userId)}`, {
    token,
  });
}

export function getPicklists(token: string, connectionId: string): Promise<PicklistsOut> {
  return apiFetch(`/api/v1/user-admin/veeva/${enc(connectionId)}/picklists`, { token });
}

export function proposeChange(token: string, input: ProposeChangeIn): Promise<ChangeOut> {
  return apiFetch("/api/v1/user-admin/changes", { method: "POST", token, json: input });
}

export function getChange(token: string, changeId: string): Promise<ChangeOut> {
  return apiFetch(`/api/v1/user-admin/changes/${enc(changeId)}`, { token });
}

export function confirmChange(token: string, changeId: string): Promise<ChangeOut> {
  return apiFetch(`/api/v1/user-admin/changes/${enc(changeId)}/confirm`, {
    method: "POST",
    token,
  });
}

export function cancelChange(token: string, changeId: string): Promise<ChangeOut> {
  return apiFetch(`/api/v1/user-admin/changes/${enc(changeId)}/cancel`, {
    method: "POST",
    token,
  });
}

export function getApproval(token: string, changeId: string): Promise<ChangeOut> {
  return apiFetch(`/api/v1/admin/approvals/veeva/${enc(changeId)}`, { token });
}

export function decideApproval(
  token: string,
  changeId: string,
  input: DecideIn,
): Promise<ChangeOut> {
  return apiFetch(`/api/v1/admin/approvals/veeva/${enc(changeId)}/decide`, {
    method: "POST",
    token,
    json: input,
  });
}

export function createAgentChat(
  token: string,
  input: CreateAgentChatIn,
): Promise<components["schemas"]["ChatOut"]> {
  return apiFetch("/api/v1/user-admin/chats", { method: "POST", token, json: input });
}

export function sendAgentMessage(
  token: string,
  chatId: string,
  input: { content: string; veeva_connection_id: string },
): Promise<AgentTurnOut> {
  return apiFetch(`/api/v1/user-admin/chats/${enc(chatId)}/messages`, {
    method: "POST",
    token,
    json: input,
  });
}

/** States in which nothing more will happen — the card stops polling. */
export const FINAL_STATUSES: readonly ChangeStatus[] = [
  "applied",
  "cancelled",
  "rejected",
  "expired",
];

/** Human labels for the Vault user fields the forms show. */
export const FIELD_LABELS: Record<string, string> = {
  user_name__v: "Username",
  user_first_name__v: "First name",
  user_last_name__v: "Last name",
  user_email__v: "Email",
  user_title__v: "Title",
  office_phone__v: "Office phone",
  mobile_phone__v: "Mobile phone",
  user_timezone__v: "Time zone",
  user_locale__v: "Locale",
  user_language__v: "Language",
  federated_id__v: "Federated id",
  security_profile__v: "Security profile",
  license_type__v: "License type",
  security_policy_id__v: "Security policy",
  active__v: "Active",
  is_domain_admin__v: "Domain admin",
};

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}
